import React, { useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import './dynamic-stepper-form.scss';
import { DynamicStepperFormProps } from './dynamicStepperFormInterface';
import { checkStepExist } from 'pages/leadGen/LeadGenConstant';
import StepperFooter from './StepperFooter';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import Logger from 'utils/logger';

const DynamicStepperForm: React.FC<DynamicStepperFormProps> = ({
  stepConfig,
  initialStep = 0,
  onFinish,
  moduleConfig,
}) => {
  const stepNumbersArray = stepConfig.map((stepObject) => Object.values(stepObject)[0].step);

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const methods = useForm<FieldValues>({
    defaultValues: {
      addressOption: 'lookup',
    },
  });

  const stepRefs: React.RefObject<any>[] = stepConfig.map(() => useRef<any>());

  useEffect(() => {
    const storedFormData = localStorage.getItem(moduleConfig.name);
    const storedCurrentStep = localStorage.getItem(`${moduleConfig.name}-currentStep`);

    methods.reset(storedFormData ? JSON.parse(storedFormData) : { addressOption: 'lookup' });
    setCurrentStep(storedCurrentStep ? parseInt(storedCurrentStep, 10) : initialStep);
  }, [initialStep]);

  const getPageNameByNumber = (pos: number, steps: DynamicStepperFormProps['stepConfig']) => {
    const stepObj = steps[pos]; // since array is 0-based
    if (!stepObj) return null;

    // get the first key (step1, step2, etc.)
    const key = Object.keys(stepObj)[0];
    return stepObj[key].pageName;
  };

  const handleNext = async (stepNumber: number) => {
    const isValid = await methods.trigger();

    if (!Object.keys(methods.formState.errors).length && isValid) {
      await executeStepAction(currentStep, 'next');
      saveFormStateToLocalStorage();

      if (typeof stepNumber == 'object') return;

      const nextStep = determineNextStep(stepNumber);

      Logger.custom('widget_page_view', {
        step: getPageNameByNumber(currentStep, stepConfig) || `Step ${nextStep}`,
        module: moduleConfig?.name || 'Dynamic Stepper Form',
        client: authenticateClientData?.data?.client || '',
        widgetName: authenticateClientData?.data?.widgetName || '',
      });
      setCurrentStep(nextStep);
    }
  };

  const determineNextStep = (stepNumber: number) => {
    return !isNaN(parseFloat(stepNumber)) && isFinite(Number(stepNumber))
      ? checkStepExist(stepNumbersArray, stepNumber) ?? currentStep + 1
      : currentStep + 1;
  };

  const handleBack = async (stepNumber: number) => {
    await executeStepAction(currentStep, 'back');
    saveFormStateToLocalStorage();

    if (typeof stepNumber == 'object' && currentStep <= stepConfig.length - 1) return;
    // if (typeof stepNumber == 'object') return;

    const previousStep = determinePreviousStep(stepNumber);

    setCurrentStep(previousStep);
  };

  const determinePreviousStep = (stepNumber: number) => {
    return !isNaN(parseFloat(stepNumber)) && isFinite(Number(stepNumber))
      ? checkStepExist(stepNumbersArray, stepNumber) ?? currentStep - 1
      : currentStep - 1;
  };

  const saveFormStateToLocalStorage = () => {
    localStorage.setItem(moduleConfig.name, JSON.stringify(methods.getValues()));
    localStorage.setItem(`${moduleConfig.name}-currentStep`, currentStep.toString());
  };

  const executeStepAction = async (step: number, action: 'next' | 'back') => {
    const stepConfigEntry = stepConfig[step];
    const stepComponentRef = stepRefs[step];

    if (
      stepConfigEntry?.[Object.keys(stepConfigEntry)[0]]?.component?.props?.callStepSpecificAction &&
      stepComponentRef
    ) {
      const actionMethod = action === 'next' ? 'nextStep' : 'backStep';
      if (!stepComponentRef.current?.hasCalledNextStep && action === 'next') {
        stepComponentRef.current.hasCalledNextStep = action === 'next';

        return stepComponentRef.current?.[actionMethod]() ?? Promise.resolve();
      }

      if (!stepComponentRef.current?.hasCalledBackStep && action === 'back') {
        stepComponentRef.current.hasCalledBackStep = action === 'back';
        return stepComponentRef.current?.[actionMethod]() ?? Promise.resolve();
      }
    }
    return Promise.resolve();
  };

  const handleStepSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (currentStep < stepConfig.length - 1) {
      methods.setValue(Object.keys(stepConfig[currentStep])[0], data);
      return;
    }

    saveFormStateToLocalStorage();
    onFinish && onFinish(methods.getValues(), methods);
  };

  return (
    <div className='container'>
      <div
        className={`dy-stepper-form ${moduleConfig?.boxShadow === false ? 'box-shadow-none' : ''} ${
          window.self !== window.top ? 'my-0' : 'with-shadow'
        }`}
      >
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleStepSubmit)}>
            {moduleConfig?.stepperHeader &&
              !stepConfig?.[currentStep]?.[Object.keys(stepConfig[currentStep])[0]]?.isHeaderHidden &&
              React.cloneElement(moduleConfig.stepperHeader as React.ReactElement, {
                heading: '60 seconds from finish',
                currentStep,
                stepConfig,
                stepNumber: stepConfig?.[currentStep]?.[Object.keys(stepConfig[currentStep])[0]]?.step,
              })}
            {stepConfig.map(
              (steps, index) =>
                index === currentStep && (
                  <div className='dy-stepper-form__body' key={index}>
                    {Object.values(steps).map((stepConfigEntry, stepIndex) => (
                      <div key={stepIndex} className='dy-stepper-form__body-inner'>
                        {React.cloneElement(stepConfigEntry.component as React.ReactElement<any>, {
                          ref: stepConfig[currentStep]?.[Object.keys(stepConfig[currentStep] ?? {})[0]]?.component
                            ?.props?.callStepSpecificAction
                            ? stepRefs[index]
                            : null,
                          onNext: index === stepConfig.length - 1 ? null : handleNext,
                          onBack: handleBack,
                        })}
                      </div>
                    ))}
                  </div>
                )
            )}
            {!stepConfig?.[currentStep]?.[Object.keys(stepConfig[currentStep])[0]]?.isFooterHidden && (
              <StepperFooter
                currentStep={currentStep}
                stepConfig={stepConfig}
                handleBack={handleBack}
                handleNext={handleNext}
              />
            )}
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default DynamicStepperForm;
